import React from 'react';
import { Phone, AccountBalance, CreditCard, TrendingUp } from '@mui/icons-material';
 
const ProductOverview = ({ customer }) => {
  if (!customer) return null;
 
  // Extract product holdings
  const productHoldings = [
    { type: 'Term Deposit', holding: customer['Term Deposit Product Holding'] },
    { type: 'Current Account', holding: customer['Current Account Product Holding'] },
    { type: 'Savings Account', holding: customer['Savings Account Product Holding'] },
    { type: 'Credit Card', holding: customer['Credit Card Product Holding'] },
    { type: 'Personal Loan', holding: customer['Personal Loan Product Holding'] },
    { type: 'Mortgage', holding: customer['Mortgage Product Holding'] },
    { type: 'Auto Loan', holding: customer['Auto Loan Product Holding'] },
    { type: 'Investment', holding: customer['Investment Product Holding'] },
    { type: 'Insurance', holding: customer['Insurance Product Holding'] }
  ].filter(product => product.holding === 1);
 
  // Calculate balances by category
  const liabilityBalance = [
    parseFloat(customer['Current Account Balance']) || 0,
    parseFloat(customer['Savings Account Balance']) || 0,
    parseFloat(customer['Term Deposit Balance']) || 0
  ].reduce((sum, amount) => sum + amount, 0);
 
  const assetBalance = [
    parseFloat(customer['Credit Card Balance']) || 0,
    parseFloat(customer['Personal Loan Balance']) || 0,
    parseFloat(customer['Auto Loan Balance']) || 0,
    parseFloat(customer['Mortgage Balance']) || 0
  ].reduce((sum, amount) => sum + amount, 0);
 
  const investmentBalance = parseFloat(customer['Investment Balance']) || 0;
 
  // Balance information categorized
  const balanceCategories = [
    {
      type: 'Liability Balance',
      amount: liabilityBalance,
      icon: <AccountBalance className="text-blue-500" />,
      details: [
        { type: 'Current Account Balance', amount: customer['Current Account Balance'] },
        { type: 'Savings Account Balance', amount: customer['Savings Account Balance'] },
        { type: 'Term Deposit Balance', amount: customer['Term Deposit Balance'] }
      ].filter(item => item.amount)
    },
    {
      type: 'Asset Balance',
      amount: assetBalance,
      icon: <CreditCard className="text-red-500" />,
      details: [
        { type: 'Credit Card Balance', amount: customer['Credit Card Balance'] },
        { type: 'Personal Loan Balance', amount: customer['Personal Loan Balance'] },
        { type: 'Auto Loan Balance', amount: customer['Auto Loan Balance'] },
        { type: 'Mortgage Balance', amount: customer['Mortgage Balance'] }
      ].filter(item => item.amount)
    },
    {
      type: 'Investment Balance',
      amount: investmentBalance,
      icon: <TrendingUp className="text-green-500" />,
      details: [
        { type: 'Investment Balance', amount: customer['Investment Balance'] }
      ].filter(item => item.amount)
    }
  ];
 
  return (
  <div className="col-span-3 grid grid-cols-3 gap-6">
        {/* Product Holdings */}
    <div className="bg-[#1A1A1A] p-6 rounded-xl border border-[#2A2A2A] shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-[#FFFFFF]">Product Holdings</h4>
        <Phone className="text-[#B4E50D]" />
      </div>
      <div className="text-2xl font-bold text-[#B4E50D] mb-4">
        Total Products: {productHoldings.length}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-[#2A2A2A]">
              <th className="px-4 py-2 text-left text-sm font-semibold text-[#B4E50D]">Product Type</th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-[#B4E50D]">Status</th>
            </tr>
          </thead>
          <tbody>
            {productHoldings.map((product, index) => (
            <tr key={index} className="border-t border-[#3A3A3A]">
            <td className="px-4 py-2 text-sm text-[#FFFFFF]">{product.type}</td>
              <td className="px-4 py-2">
              <span className="px-2 py-1 bg-[#B4E50D] text-[#0A0A0A] rounded-full text-xs font-medium">
                Active
              </span>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
    
          {/* Balance Information */}
    <div className="bg-[#1A1A1A] p-6 rounded-xl border border-[#2A2A2A] shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-[#FFFFFF]">Balance Information</h4>
        <AccountBalance className="text-[#B4E50D]" />
      </div>
      <div className="space-y-6">
        {balanceCategories.map((category, index) => (
        <div key={index} className="border-b border-[#3A3A3A] pb-4 last:border-b-0">
          <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
                            {category.icon}
          <h5 className="font-medium text-[#FFFFFF]">{category.type}</h5>
          </div>
          <span className="text-lg font-bold text-[#B4E50D]">
                            ${category.amount.toLocaleString()}
          </span>
          </div>
          <div className="space-y-1">
            {category.details.map((item, idx) => (
          <div key={idx} className="flex justify-between text-sm text-[#B0B0B0]">
            <span>{item.type}</span>
            <span className="text-[#FFFFFF]">
              ${parseFloat(item.amount).toLocaleString()}
            </span>
          </div>
          ))}
          </div>
        </div>
      ))}
      </div>
    </div>
    
    {/* Next Best Product Recommendation */}
    <div className="bg-[#1A1A1A] p-6 rounded-xl border border-[#2A2A2A] shadow-xl">
      <h4 className="text-lg font-semibold mb-4 text-[#FFFFFF]">Next Best Product Recommendation</h4>
      <div className="space-y-4">
        {customer['Recommended Products'] ? (
          customer['Recommended Products'].split(',').map((product, index) => (
        <div key={index} className="p-4 bg-[#2A2A2A] rounded-lg border border-[#3A3A3A]">
          <h5 className="font-medium text-[#B4E50D]">{product.trim()}</h5>
          <p className="text-sm text-[#B0B0B0] mt-1">
            Recommendation basis customer product take up propensity
          </p>
        </div>
          ))
        ) : (
          <p className="text-[#B0B0B0]">No recommendations available</p>
          )}
      </div>
    </div>

  </div>
  );
};
 
export default ProductOverview;